/**
 * 答题自动保存服务
 * 
 * 核心功能：
 * 1. 每30秒定时采集答题状态数据并静默提交
 * 2. 防抖处理避免高频无效请求
 * 3. 网络异常指数退避重试（最多3次）
 * 4. localStorage本地缓存，确保页面刷新/关闭时数据可恢复
 * 5. 重新加载时自动同步本地缓存到服务器
 */

// ============ 类型定义 ============

export interface AutoSaveData {
  answers: Record<string, string>;
  currentIndex: number;
  timeLeft: number;
  markedQuestions: number[];
  isSubmitted: boolean;
  score: number;
  aiExplanations: Record<string, string>;
  savedAt: string;
}

export type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error' | 'syncing';

export interface AutoSaveStatusInfo {
  status: AutoSaveStatus;
  lastSavedTime: Date | null;
  errorMessage: string | null;
}

type StatusCallback = (info: AutoSaveStatusInfo) => void;
type DataCollector = () => AutoSaveData;
type SaveFunction = (data: AutoSaveData) => Promise<boolean>;

// ============ 配置常量 ============

const AUTO_SAVE_INTERVAL_MS = 30_000; // 30秒
const DEBOUNCE_DELAY_MS = 2_000;      // 2秒防抖
const MAX_RETRY_COUNT = 3;             // 最大重试次数
const BASE_RETRY_DELAY_MS = 1_000;    // 基础重试延迟1秒
const LOCAL_STORAGE_KEY_PREFIX = 'autosave_';

// ============ AutoSaveService 类 ============

export class AutoSaveService {
  private intervalTimer: ReturnType<typeof setInterval> | null = null;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private retryCount = 0;
  private isSaving = false;
  private lastSavedTime: Date | null = null;
  private errorMessage: string | null = null;
  private status: AutoSaveStatus = 'idle';
  private statusCallbacks: Set<StatusCallback> = new Set();
  // 使用ref存储函数引用，确保闭包始终获取最新state
  private dataCollectorRef: { current: DataCollector | null } = { current: null };
  private saveFunctionRef: { current: SaveFunction | null } = { current: null };
  private storageKey: string;
  private pendingRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private lastDataHash: string = '';
  private destroyed = false;

  constructor(examKey: string) {
    this.storageKey = `${LOCAL_STORAGE_KEY_PREFIX}${examKey}`;
  }

  // ============ 公共方法 ============

  /**
   * 初始化自动保存服务
   */
  init(
    dataCollector: DataCollector,
    saveFunction: SaveFunction,
    onStatusChange?: StatusCallback
  ) {
    this.dataCollectorRef.current = dataCollector;
    this.saveFunctionRef.current = saveFunction;
    if (onStatusChange) {
      this.statusCallbacks.add(onStatusChange);
    }

    // 启动定时保存
    this.startInterval();

    // 尝试同步本地缓存（页面刷新恢复场景）
    this.syncLocalCache();
  }

  /**
   * 更新数据采集函数（解决闭包引用问题）
   * 每次React组件re-render时调用，确保service内部始终获取最新state
   */
  updateDataCollector(dataCollector: DataCollector) {
    this.dataCollectorRef.current = dataCollector;
  }

  /**
   * 更新保存函数（解决闭包引用问题）
   */
  updateSaveFunction(saveFunction: SaveFunction) {
    this.saveFunctionRef.current = saveFunction;
  }

  /**
   * 注册状态变更回调
   */
  onStatusChange(callback: StatusCallback) {
    this.statusCallbacks.add(callback);
    callback(this.getStatusInfo());
  }

  /**
   * 移除状态变更回调
   */
  offStatusChange(callback: StatusCallback) {
    this.statusCallbacks.delete(callback);
  }

  /**
   * 手动触发一次保存
   */
  async saveNow(): Promise<boolean> {
    const collector = this.dataCollectorRef.current;
    const saver = this.saveFunctionRef.current;
    if (!collector || !saver) return false;
    if (this.isSaving) return false;

    const data = collector();
    this.writeLocalCache(data);
    return this.doSave(data);
  }

  /**
   * 答题变更时触发防抖保存
   */
  triggerDebouncedSave() {
    if (this.destroyed) return;
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.debouncedSave();
    }, DEBOUNCE_DELAY_MS);
  }

  /**
   * 获取当前状态信息
   */
  getStatusInfo(): AutoSaveStatusInfo {
    return {
      status: this.status,
      lastSavedTime: this.lastSavedTime,
      errorMessage: this.errorMessage,
    };
  }

  /**
   * 获取本地缓存数据
   */
  getLocalCacheData(): AutoSaveData | null {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) return null;
      const data = JSON.parse(raw) as AutoSaveData;
      const savedAt = new Date(data.savedAt);
      const hoursDiff = (Date.now() - savedAt.getTime()) / (1000 * 60 * 60);
      if (hoursDiff > 24) {
        localStorage.removeItem(this.storageKey);
        return null;
      }
      return data;
    } catch {
      localStorage.removeItem(this.storageKey);
      return null;
    }
  }

  /**
   * 清除本地缓存
   */
  clearLocalCache() {
    localStorage.removeItem(this.storageKey);
  }

  /**
   * 重置自动保存状态（用于重新开始答题时）
   * 清空lastSavedTime、errorMessage，将状态重置为idle
   */
  reset() {
    this.lastSavedTime = null;
    this.errorMessage = null;
    this.status = 'idle';
    this.lastDataHash = '';
    this.retryCount = 0;
    this.isSaving = false;
    this.updateStatus('idle');
  }

  /**
   * 销毁服务
   */
  destroy() {
    this.destroyed = true;
    if (this.intervalTimer) {
      clearInterval(this.intervalTimer);
      this.intervalTimer = null;
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    if (this.pendingRetryTimer) {
      clearTimeout(this.pendingRetryTimer);
      this.pendingRetryTimer = null;
    }
    this.statusCallbacks.clear();
  }

  // ============ 内部方法 ============

  private startInterval() {
    if (this.intervalTimer) {
      clearInterval(this.intervalTimer);
    }
    this.intervalTimer = setInterval(() => {
      this.periodicSave();
    }, AUTO_SAVE_INTERVAL_MS);
  }

  private async periodicSave() {
    if (this.destroyed || this.isSaving) return;
    const collector = this.dataCollectorRef.current;
    const saver = this.saveFunctionRef.current;
    if (!collector || !saver) return;

    const data = collector();
    const hash = this.computeHash(data);

    // 数据未变化：仍更新时间戳并通知UI，但不发送网络请求
    if (hash === this.lastDataHash) {
      this.lastSavedTime = new Date();
      this.updateStatus('saved');
      return;
    }

    this.lastDataHash = hash;
    this.writeLocalCache(data);
    await this.doSave(data);
  }

  private async debouncedSave() {
    if (this.destroyed || this.isSaving) return;
    const collector = this.dataCollectorRef.current;
    if (!collector) return;

    const data = collector();
    this.writeLocalCache(data);
    await this.doSave(data);
  }

  private async doSave(data: AutoSaveData): Promise<boolean> {
    const saver = this.saveFunctionRef.current;
    if (!saver) return false;
    this.isSaving = true;
    this.retryCount = 0;
    this.updateStatus('saving');

    let success = false;
    while (this.retryCount <= MAX_RETRY_COUNT && !this.destroyed) {
      try {
        success = await saver(data);
        if (success) {
          this.lastSavedTime = new Date();
          this.errorMessage = null;
          this.updateStatus('saved');
          this.isSaving = false;
          return true;
        }
      } catch (error: any) {
        this.errorMessage = error?.message || '保存失败';
      }

      this.retryCount++;
      if (this.retryCount <= MAX_RETRY_COUNT && !this.destroyed) {
        const delay = BASE_RETRY_DELAY_MS * Math.pow(2, this.retryCount - 1);
        this.updateStatus('error');
        await this.sleep(delay);
      }
    }

    this.updateStatus('error');
    this.isSaving = false;
    return false;
  }

  private async syncLocalCache() {
    const cachedData = this.getLocalCacheData();
    if (!cachedData) return;
    const saver = this.saveFunctionRef.current;
    if (!saver) return;

    if (cachedData.isSubmitted) return;

    this.updateStatus('syncing');
    try {
      const success = await saver(cachedData);
      if (success) {
        this.lastSavedTime = new Date(cachedData.savedAt);
        this.errorMessage = null;
        this.updateStatus('saved');
      } else {
        this.updateStatus('error');
      }
    } catch {
      this.updateStatus('error');
    }
  }

  private writeLocalCache(data: AutoSaveData) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(data));
    } catch {
      // localStorage空间不足等异常，静默处理
    }
  }

  private computeHash(data: AutoSaveData): string {
    // 排除timeLeft（每秒变化不应触发保存）和score/aiExplanations（提交后才变化）
    const coreData = {
      answers: data.answers,
      currentIndex: data.currentIndex,
      markedQuestions: data.markedQuestions,
      isSubmitted: data.isSubmitted,
    };
    return JSON.stringify(coreData);
  }

  private updateStatus(newStatus: AutoSaveStatus) {
    this.status = newStatus;
    const info = this.getStatusInfo();
    this.statusCallbacks.forEach(cb => {
      try { cb(info); } catch { /* 防止回调异常影响主流程 */ }
    });
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => {
      this.pendingRetryTimer = setTimeout(resolve, ms);
    });
  }
}

// ============ React Hook ============

import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * useAutoSave Hook
 * 
 * 关键设计：使用ref存储dataCollector和saveFunction，
 * 每次re-render时更新ref，确保service内部始终获取最新state，
 * 解决React闭包引用问题。
 */
export function useAutoSave(
  examKey: string,
  dataCollector: DataCollector,
  saveFunction: SaveFunction
) {
  const [statusInfo, setStatusInfo] = useState<AutoSaveStatusInfo>({
    status: 'idle',
    lastSavedTime: null,
    errorMessage: null,
  });
  const serviceRef = useRef<AutoSaveService | null>(null);
  const dataCollectorRef = useRef(dataCollector);
  const saveFunctionRef = useRef(saveFunction);

  // 每次re-render时更新ref，确保service内部始终获取最新闭包
  dataCollectorRef.current = dataCollector;
  saveFunctionRef.current = saveFunction;

  // 创建service（仅首次）
  if (!serviceRef.current) {
    serviceRef.current = new AutoSaveService(examKey);
  }

  const service = serviceRef.current;

  // 初始化service（仅examKey变化时）
  useEffect(() => {
    // 使用ref包装的函数，确保service内部调用时获取最新闭包
    const wrappedCollector: DataCollector = () => dataCollectorRef.current();
    const wrappedSaver: SaveFunction = (data) => saveFunctionRef.current(data);

    service.init(wrappedCollector, wrappedSaver, (info) => {
      setStatusInfo(info);
    });

    return () => {
      service.destroy();
      serviceRef.current = null;
    };
  }, [examKey]);

  // 每次re-render时更新service内部的函数引用
  useEffect(() => {
    if (serviceRef.current && !serviceRef.current.getStatusInfo().status) {
      // service已初始化，更新函数引用
    }
    // 通过wrapped函数间接引用，ref已在上层更新，无需额外操作
  });

  // 页面关闭前保存
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const data = dataCollectorRef.current();
      service.writeLocalCache(data);

      if (service.getStatusInfo().status === 'error' || service.getStatusInfo().status === 'saving') {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [service]);

  const triggerDebouncedSave = useCallback(() => {
    service.triggerDebouncedSave();
  }, [service]);

  const saveNow = useCallback(async (): Promise<boolean> => {
    return service.saveNow();
  }, [service]);

  const getLocalCacheData = useCallback((): AutoSaveData | null => {
    return service.getLocalCacheData();
  }, [service]);

  const clearLocalCache = useCallback(() => {
    service.clearLocalCache();
  }, [service]);

  const resetAutoSave = useCallback(() => {
    service.reset();
  }, [service]);

  return {
    statusInfo,
    triggerDebouncedSave,
    saveNow,
    getLocalCacheData,
    clearLocalCache,
    resetAutoSave,
  };
}
